import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Download, Search } from 'lucide-react';
import { criticalityAccent } from '@/components/ui/mobile-card';
import {
  equipmentApi,
  type Equipment,
  type EquipmentImportResolved,
  type EquipmentImportPreviewRow,
  type EquipmentImportCommitRow,
} from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Badge, criticalityColor } from '@/components/ui/badge';
import { Table, Thead, Tbody, Th, Td, Tr, SortableTh, type SortState } from '@/components/ui/table';
import { Dialog } from '@/components/ui/dialog';
import { PreviewImportDialog } from '@/components/PreviewImportDialog';
import { StagedAttachments } from '@/components/StagedAttachments';
import { uploadStagedAttachments } from '@/lib/attachments';
import { exportToExcel, exportToCsv, downloadEquipmentTemplate } from '@/lib/excel';
import { PageHeader } from '@/components/ui/page-header';

const BLANK: Partial<Equipment> = {
  asset_tag: '', name: '', type: '', location: '',
  criticality: 'medium', manufacturer: '', model: '', serial_number: '', is_active: true,
};

export function EquipmentPage() {
  const { isAdmin, hasPro } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Equipment | null>(null);
  const [form, setForm] = useState<Partial<Equipment>>(BLANK);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ by: 'asset_tag', dir: 'asc' });

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const onSort = (next: SortState) => { setSort(next); setPage(1); };

  const { data, isLoading } = useQuery({
    queryKey: ['equipment', page, search, sort.by, sort.dir],
    queryFn: () => equipmentApi.list(page, 50, search || undefined, sort.by, sort.dir),
  });

  const save = useMutation({
    mutationFn: async (): Promise<{ failed: string[] }> => {
      if (editing) { await equipmentApi.update(editing.id, form); return { failed: [] }; }
      const created = await equipmentApi.create(form);
      const { failed } = await uploadStagedAttachments('equipment', created.id, stagedFiles);
      return { failed };
    },
    onSuccess: ({ failed }) => {
      qc.invalidateQueries({ queryKey: ['equipment'] });
      closeDialog();
      if (failed.length) {
        alert(`${failed.length} file${failed.length > 1 ? 's' : ''} couldn't be uploaded: ${failed.join(', ')}. You can add ${failed.length > 1 ? 'them' : 'it'} from the equipment record.`);
      }
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => equipmentApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
  });

  const openCreate = () => { setEditing(null); setForm(BLANK); setStagedFiles([]); setOpen(true); };
  const openEdit = (e: Equipment) => { setEditing(e); setForm(e); setStagedFiles([]); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditing(null); setForm(BLANK); setStagedFiles([]); };
  const set = (k: keyof Equipment, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const totalCount = data?.pagination.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Equipment"
        tag={totalCount > 0 ? `${totalCount} asset${totalCount === 1 ? '' : 's'}` : undefined}
        actions={
          isAdmin ? <Button size="sm" onClick={openCreate}><Plus size={14} /> Add Equipment</Button> : undefined
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm border border-canvas-line bg-white rounded-lg w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        {(['Excel', 'CSV'] as const).map(fmt => (
                <Button
                  key={fmt}
                  variant="outline" size="sm"
                  onClick={async () => {
                    const all = await equipmentApi.list(1, 5000);
                    const rows = all.data.map(eq => ({
                      asset_tag:     eq.asset_tag,
                      name:          eq.name,
                      type:          eq.type,
                      location:      eq.location,
                      criticality:   eq.criticality,
                      manufacturer:  eq.manufacturer ?? '',
                      model:         eq.model ?? '',
                      serial_number: eq.serial_number ?? '',
                      install_date:  eq.install_date ? new Date(eq.install_date).toLocaleDateString() : '',
                      notes:         eq.notes ?? '',
                      is_active:     eq.is_active ? 'true' : 'false',
                    }));
                    fmt === 'Excel' ? exportToExcel(rows, 'equipment_export') : exportToCsv(rows, 'equipment_export');
                  }}
                >
                  <Download size={14} /> {fmt}
                </Button>
              ))}
              {isAdmin && (
                <PreviewImportDialog<EquipmentImportResolved>
                  buttonLabel="Import"
                  dialogTitle="Import Equipment"
                  noun="piece of equipment"
                  nounPlural="pieces of equipment"
                  onDownloadTemplate={downloadEquipmentTemplate}
                  fields={[
                    { key: 'asset_tag',     label: 'Asset Tag',     required: false, description: 'Unique ID e.g. PUMP-001 (auto-generated if blank)' },
                    { key: 'name',          label: 'Name',          required: true,  description: 'Display name' },
                    { key: 'type',          label: 'Type',          required: true,  description: 'pump / motor / conveyor / compressor / fan / other' },
                    { key: 'location',      label: 'Location',      required: true,  description: 'Physical location' },
                    { key: 'criticality',   label: 'Criticality',   required: false, description: 'low / medium / high / critical (defaults to medium)' },
                    { key: 'manufacturer',  label: 'Manufacturer',  description: 'Manufacturer name' },
                    { key: 'model',         label: 'Model',         description: 'Model number' },
                    { key: 'serial_number', label: 'Serial Number', description: 'Serial number (used as tag fallback)' },
                    { key: 'install_date',  label: 'Install Date',  description: 'YYYY-MM-DD format' },
                  ]}
                  previewColumns={[
                    { header: 'Asset Tag', render: r => r.resolved?.asset_tag ?? <span className="text-slate-400">(auto)</span> },
                    { header: 'Name',      render: r => r.resolved?.name ?? '—' },
                    { header: 'Type',      render: r => r.resolved?.type ?? '—' },
                    { header: 'Location',  render: r => r.resolved?.location ?? '—' },
                    { header: 'Criticality', render: r => r.resolved?.criticality ?? '—' },
                  ]}
                  importPreview={rows => equipmentApi.importPreview(rows as unknown as EquipmentImportPreviewRow[])}
                  importCommit={rows => equipmentApi.importCommit(rows as unknown as EquipmentImportCommitRow[])}
                  onComplete={() => qc.invalidateQueries({ queryKey: ['equipment'] })}
                />
              )}
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-slate-400">Loading…</p>
      ) : (
        <>
              {/* Mobile + tablet: card grid */}
              <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-2">
                {data?.data.length === 0 && (
                  <p className="text-center text-slate-400 text-sm py-6 col-span-full">No equipment found.</p>
                )}
                {data?.data.map(eq => (
                  <div
                    key={eq.id}
                    onClick={() => navigate(`/equipment/${eq.id}`)}
                    className="bg-white rounded-lg border border-slate-200 p-3 cursor-pointer transition-colors active:bg-slate-50"
                    style={{ borderLeftWidth: '3px', borderLeftColor: criticalityAccent(eq.criticality) }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-slate-900 text-sm leading-snug truncate">{eq.name}</span>
                      <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                        <Badge color={criticalityColor(eq.criticality)}>{eq.criticality}</Badge>
                        <Badge color={eq.is_active ? 'green' : 'slate'}>
                          {eq.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      <span className="font-mono">{eq.asset_tag}</span>
                      {eq.serial_number && (
                        <> · <span className="font-mono">{eq.serial_number}</span></>
                      )}
                      {' · '}{eq.location}
                    </p>
                    {eq.current_reading != null && (
                      <p className="text-xs text-slate-500 mt-1 font-mono">
                        {eq.current_reading.toLocaleString()}{eq.meter_unit ? ` ${eq.meter_unit}` : ''}
                      </p>
                    )}
                    {isAdmin && (
                      <div
                        className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100"
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          onClick={() => openEdit(eq)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-slate-100 text-slate-600"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          onClick={() => { if (confirm(`Delete ${eq.name}?`)) del.mutate(eq.id); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="hidden lg:block">
            <Table variant="cards">
              <Thead>
                <tr>
                  <SortableTh column="asset_tag"     sort={sort} onSort={onSort}>Asset Tag</SortableTh>
                  <SortableTh column="serial_number" sort={sort} onSort={onSort}>Serial #</SortableTh>
                  <SortableTh column="name"          sort={sort} onSort={onSort}>Name</SortableTh>
                  <SortableTh column="type"          sort={sort} onSort={onSort}>Type</SortableTh>
                  <SortableTh column="manufacturer"  sort={sort} onSort={onSort}>Manufacturer</SortableTh>
                  <SortableTh column="model"         sort={sort} onSort={onSort}>Model</SortableTh>
                  <SortableTh column="location"      sort={sort} onSort={onSort}>Location</SortableTh>
                  <SortableTh column="criticality"   sort={sort} onSort={onSort}>Criticality</SortableTh>
                  <Th className="text-right">Meter</Th>
                  <SortableTh column="is_active"     sort={sort} onSort={onSort}>Status</SortableTh>
                  {isAdmin && <Th className="w-20">Actions</Th>}
                </tr>
              </Thead>
              <Tbody>
                {data?.data.map(eq => (
                  <Tr
                    key={eq.id}
                    onClick={e => {
                      if ((e.target as HTMLElement).closest('button, a')) return;
                      navigate(`/equipment/${eq.id}`);
                    }}
                  >
                    <Td className="font-mono text-xs">{eq.asset_tag}</Td>
                    <Td className="font-mono text-xs text-slate-500">{eq.serial_number ?? '—'}</Td>
                    <Td>
                      <Link
                        to={`/equipment/${eq.id}`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {eq.name}
                      </Link>
                    </Td>
                    <Td>{eq.type}</Td>
                    <Td className="text-xs">{eq.manufacturer ?? '—'}</Td>
                    <Td className="font-mono text-xs">{eq.model ?? '—'}</Td>
                    <Td>{eq.location}</Td>
                    <Td><Badge color={criticalityColor(eq.criticality)}>{eq.criticality}</Badge></Td>
                    <Td className="text-right font-mono text-xs">
                      {eq.current_reading != null
                        ? `${eq.current_reading.toLocaleString()}${eq.meter_unit ? ' ' + eq.meter_unit : ''}`
                        : <span className="text-slate-300">—</span>}
                    </Td>
                    <Td>
                      <Badge color={eq.is_active ? 'green' : 'slate'}>
                        {eq.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    {isAdmin && (
                      <Td>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(eq)} className="p-1 rounded hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>
                          <button
                            onClick={() => { if (confirm(`Delete ${eq.name}?`)) del.mutate(eq.id); }}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
                {data?.data.length === 0 && (
                  <Tr><Td className="py-8 text-center text-slate-400" colSpan={isAdmin ? 11 : 10}>No equipment found.</Td></Tr>
                )}
              </Tbody>
            </Table>
              </div>
            </>
          )}

      {/* Pagination */}
      {(data?.pagination.total ?? 0) > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 50 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={open} onClose={closeDialog} title={editing ? 'Edit Equipment' : 'Add Equipment'}>
        <form
          onSubmit={e => { e.preventDefault(); save.mutate(); }}
          className="flex flex-col gap-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <Input label="Asset Tag" value={form.asset_tag ?? ''} onChange={e => set('asset_tag', e.target.value)} placeholder="Auto-generated if blank" />
            <Input label="Name *" value={form.name ?? ''} onChange={e => set('name', e.target.value)} required />
            <Input label="Type *" value={form.type ?? ''} onChange={e => set('type', e.target.value)} required placeholder="Pump / Motor…" />
            <Input label="Location *" value={form.location ?? ''} onChange={e => set('location', e.target.value)} required />
            <Select label="Criticality" value={form.criticality ?? 'medium'} onChange={e => set('criticality', e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Select>
            <Select label="Status" value={form.is_active ? 'true' : 'false'} onChange={e => set('is_active', e.target.value === 'true')}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input label="Manufacturer" value={form.manufacturer ?? ''} onChange={e => set('manufacturer', e.target.value)} />
            <Input label="Model" value={form.model ?? ''} onChange={e => set('model', e.target.value)} />
            <Input label="Serial Number" value={form.serial_number ?? ''} onChange={e => set('serial_number', e.target.value)} />
          </div>

          {/* Meter tracking — Pro feature for meter-based PMs */}
          {hasPro && (
            <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
              <Select
                label="Meter Unit (optional)"
                value={form.meter_unit ?? ''}
                onChange={e => set('meter_unit', e.target.value || null)}
              >
                <option value="">No meter</option>
                <option value="hours">Hours (operating)</option>
                <option value="cycles">Cycles</option>
                <option value="miles">Miles</option>
                <option value="km">Kilometers</option>
                <option value="rpm-hours">RPM-hours</option>
              </Select>
              <Input
                label="Current Reading (optional)"
                type="number" min={0} step="any"
                value={form.current_reading ?? ''}
                onChange={e => set('current_reading', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="Initial meter value at install"
                disabled={!form.meter_unit}
              />
            </div>
          )}

          {!editing && (
            <div className="border-t border-slate-200 pt-3">
              <StagedAttachments files={stagedFiles} onChange={setStagedFiles} label="Attachments (optional)" />
            </div>
          )}

          {save.error && <p className="text-sm text-red-600">{(save.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
